import React, { useState, useEffect } from "react";

const GenerateAIModal = ({ isOpen, onClose, onGenerate }) => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) fetchEvents();
  }, [isOpen]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/events?upcoming=true", { headers: { Accept: "application/json" } });
      if (response.ok) {
        const data = await response.json();
        const list = data.events || data || [];
        setEvents(list);
        if (list.length > 0) setSelectedEventId(list[0].id);
      } else {
        setError("Failed to load events");
      }
    } catch (err) {
      setError("Error loading events: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedEventId) {
      setError("Please select an event");
      return;
    }
    setGenerating(true);
    setError(null);
    try {
      const response = await fetch("/api/google_ads_ai/reports", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content"),
        },
        body: JSON.stringify({ event_id: selectedEventId }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        sessionStorage.setItem("googleAdsAI_generating", "true");
        sessionStorage.setItem("googleAdsAI_report_id", data.report_id);
        sessionStorage.setItem("googleAdsAI_generation_start", Date.now().toString());
        onGenerate?.(data);
        onClose();
        if (window.__googleAdsGenerationQueueRefresh) window.__googleAdsGenerationQueueRefresh();
      } else {
        setError(data.error || "Failed to start generation");
      }
    } catch (err) {
      setError("Error starting generation: " + err.message);
    } finally {
      setGenerating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "560px", maxHeight: "80vh", overflow: "hidden", boxShadow: "0 20px 50px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>Generate AI Campaign</h2>
            <p style={{ margin: "4px 0 0 0", fontSize: "14px", color: "#6b7280" }}>Select an event to create AI-powered Google Ads</p>
          </div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280", padding: "0 4px" }}>×</button>
        </div>
        <div style={{ padding: "24px", maxHeight: "400px", overflowY: "auto" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#6b7280" }}>Loading events...</div>
          ) : events.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <p style={{ color: "#6b7280", marginBottom: "16px" }}>No upcoming events found.</p>
              <p style={{ fontSize: "14px", color: "#9ca3af" }}>Create an event first to generate AI-powered ads.</p>
            </div>
          ) : (
            <>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", fontSize: "14px" }}>Select Event</label>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {events.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEventId(event.id)}
                    style={{
                      padding: "14px 16px",
                      border: selectedEventId === event.id ? "2px solid #4285F4" : "1px solid #e5e7eb",
                      borderRadius: "8px",
                      cursor: "pointer",
                      background: selectedEventId === event.id ? "#e8f0fe" : "#fff",
                      transition: "all 0.15s",
                    }}
                  >
                    <div style={{ fontWeight: "500", marginBottom: "4px" }}>{event.name}</div>
                    <div style={{ fontSize: "13px", color: "#6b7280" }}>
                      {event.start_date && new Date(event.start_date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                      {event.venue_name && ` • ${event.venue_name}`}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          {error && (
            <div style={{ marginTop: "16px", padding: "12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "6px", color: "#dc2626", fontSize: "14px" }}>{error}</div>
          )}
        </div>
        {selectedEventId && (
          <div style={{ margin: "0 24px 16px", padding: "12px 16px", background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: "8px", fontSize: "13px", color: "#2e7d32" }}>
            <strong>AI will generate:</strong>
            <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
              <li>Marketing plan for Google Search</li>
              <li>Campaign and ad group structure</li>
              <li>Responsive Search Ad headlines & descriptions</li>
            </ul>
          </div>
        )}
        <div style={{ padding: "16px 24px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <button type="button" onClick={onClose} disabled={generating} style={{ padding: "10px 20px", background: "#fff", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "14px", cursor: generating ? "not-allowed" : "pointer" }}>Cancel</button>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !selectedEventId || loading}
            style={{
              padding: "10px 24px",
              background: generating || !selectedEventId ? "#9ca3af" : "#4285F4",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: generating || !selectedEventId ? "not-allowed" : "pointer",
            }}
          >
            {generating ? "Starting..." : "Generate with AI"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateAIModal;
